/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { iif, patch } from '@ngxs/store/operators';
import { merge } from 'lodash-es';
import { of } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { TableLimits } from '../../../core/state/appConfig/app-config.model';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { ShowError } from '../../../core/state/notification/notification.actions';
import { DirectUrlResponse } from '../../../types/FileDownload';
import { ImageSize } from '../../../types/Image';
import { PagingOptions } from '../../../types/PagingOptions';
import { SortOptions } from '../../../types/SortOptions';
import { VideoUrl } from '../../../types/StorageUrl';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { LoggerService } from '../../shared/services/logger.service';
import { RestService } from '../../shared/services/rest.service';
import {
    ClearParcelDisplayOptions,
    ClearReportFilters,
    ClearReportOptions,
    ClearReports,
    DisableTableEdit,
    EnableTableEdit,
    LaunchVideo,
    LoadDataReports,
    LoadFeatureReports,
    LoadFeatureReportsComplete,
    RefreshFilterFields,
    RequestVideoUrl,
    ResetReportPagingAndSort,
    SelectDataReport,
    SelectDataReportByName,
    SelectFeatureReport,
    SelectFeatureReportByName,
    SelectReport,
    SelectReportByName,
    SetActiveDataReports,
    SetActiveFeatureReports,
    SetFeatureReportKeys,
    SetFilterOptions,
    SetPagingOptions,
    SetParcelDisplayAddress,
    SetParcelImageSize,
    SetParcelImageUrl,
    SetParcelPano,
    SetSortOptions
} from './report.actions';
import {
    DataReportDefinitionCollection,
    DataReportLaunchOptions,
    FeatureReportCollection,
    FeatureReportDefinitionCollection,
    FilterOptions,
    ParcelDisplayOptions,
    ReportDisplayOptionsDictionary,
    ReportLaunchOptions,
    ReportOptions,
    ReportOptionsDictionary,
    ReportStateModel,
    SetFeatureReportKeysPayload
} from './report.model';

@State<ReportStateModel>({
    name: 'reports',
    defaults: {
        definitions: {
            feature: {},
            featureReportsLoaded: false,
            data: [],
            dataReportsLoaded: false,
        },
        activeFeatureReports: {},
        activeDataReports: [],
        selectedDataReport: null,
        selectedFeatureReports: {},
        featureReportKeys: {},
        loadingFeatureReports: false,
        options: {},
    },
})
@Injectable()
export class ReportState {
    constructor(private logger: LoggerService, private rest: RestService, private store: Store) {}

    @Action(ClearReports)
    clearReports({ getState, setState }: StateContext<ReportStateModel>, action: ClearReports) {
        const state: ReportStateModel = DashGisUtil.clone(getState());
        if (!action.payload || action.payload.type === 'data') {
            state.activeDataReports = [];
            state.selectedDataReport = null;
        }
        if (!action.payload || action.payload.type === 'feature') {
            state.activeFeatureReports = {};
            state.selectedFeatureReports = {};
        }
        setState(state);
    }

    @Action(LoadDataReports)
    loadDataReports({ getState, setState }: StateContext<ReportStateModel>) {
        return this.rest.apiGet<DataReportDefinitionCollection>('report/data/definitions', null).pipe(
            tap((reportDefinitions) => {
                const state: ReportStateModel = DashGisUtil.clone(getState());
                state.definitions.data = reportDefinitions;
                state.definitions.dataReportsLoaded = true;
                setState(state);
            })
        );
    }

    @Action(SetActiveDataReports)
    setActiveDataReports({ getState, setState }: StateContext<ReportStateModel>) {
        const state: ReportStateModel = DashGisUtil.clone(getState());
        const dataReports = DashGisUtil.clone(state.definitions.data);
        state.activeDataReports = dataReports.filter((d) => !d.hidden).map((d) => ({ definition: d }));
        if (state.activeDataReports.length === 1) {
            state.selectedDataReport = state.activeDataReports[0];
        }
        setState(state);
    }

    @Action(LoadFeatureReports)
    loadFeatureReports({ setState, dispatch }: StateContext<ReportStateModel>, action: LoadFeatureReports) {
        const params = {
            layers: action.payload.layerNames.join(','),
        };
        setState(patch({ loadingFeatureReports: true }));
        const route = `report/feature/definition/${action.payload.mapName}`;
        return this.rest.apiGet<FeatureReportDefinitionCollection>(route, params).pipe(
            tap((definitions) => {
                setState(patch({
                    definitions: patch({
                        feature: definitions,
                        featureReportsLoaded: true,
                    }),
                }))
            }),
            tap(() => dispatch(new LoadFeatureReportsComplete())),
        );
    }

    @Action(LoadFeatureReportsComplete)
    loadFeatureReportsComplete({ setState }: StateContext<ReportStateModel>) {
        setState(patch({ loadingFeatureReports: false }));
    }

    @Action(SetActiveFeatureReports)
    setActiveFeatureReports({ getState, setState }: StateContext<ReportStateModel>, action: SetActiveFeatureReports) {
        const state: ReportStateModel = DashGisUtil.clone(getState());
        const featureReports: FeatureReportCollection = {};
        Object.getOwnPropertyNames(action.payload.selection).forEach((layerName) => {
            // we only do detail reports when single selection for layer, otherwise
            // feature type reports
            const selectionCount = action.payload.selection[layerName].length;
            if (state.definitions.feature.hasOwnProperty(layerName)) {
                const reportDefinitions = state.definitions.feature[layerName].filter((r) => {
                    // exclude hidden reports
                    if (r.hidden) {
                        return false;
                    }

                    return selectionCount === 1
                        ? r.subType.toLowerCase() === 'detail'
                        : r.subType.toLowerCase() !== 'detail';
                });

                if (reportDefinitions && reportDefinitions.length > 0) {
                    featureReports[layerName] = reportDefinitions.map((d) => ({ definition: d }));
                }
            }
        });
        state.activeFeatureReports = DashGisUtil.clone(featureReports);
        setState(state);
    }

    @Action(SetFeatureReportKeys)
    setFeatureReportKeys({ getState, setState }: StateContext<ReportStateModel>, action: SetFeatureReportKeys) {
        const state = DashGisUtil.clone(getState());
        const layer = action.payload.layer;
        state.featureReportKeys[layer] = action.payload.keys;
        setState(state);
    }

    @Action(SelectReport)
    selectReport({ dispatch }: StateContext<ReportStateModel>, action: SelectReport) {
        switch (action.payload.selectedReport.definition.type) {
            case 'feature':
                dispatch(new SelectFeatureReport(action.payload));
                break;
            case 'data':
                dispatch(new SelectDataReport(action.payload));
                break;
        }
    }

    @Action(SelectDataReport)
    selectDataReport({ getState, setState }: StateContext<ReportStateModel>, action: SelectDataReport) {
        const state: ReportStateModel = DashGisUtil.clone(getState());
        state.selectedDataReport = action.payload.selectedReport;
        setState(state);
    }

    @Action(SelectFeatureReport)
    selectFeatureReport({ getState, setState }: StateContext<ReportStateModel>, action: SelectFeatureReport) {
        const state: ReportStateModel = DashGisUtil.clone(getState());
        const layer = action.payload.selectedReport.definition.layer;
        state.selectedFeatureReports[layer] = action.payload.selectedReport;
        setState(state);
    }

    @Action(SelectReportByName)
    selectReportByName({ getState, setState, dispatch }: StateContext<ReportStateModel>, action: SelectReportByName) {
        const state: ReportStateModel = getState();
        const report = this.findReport(action.payload.reportName, state);
        if (report) {
            if (report.definition.type.toLowerCase() === 'feature') {
                return dispatch(new SelectFeatureReportByName(action.payload));
            } else {
                return dispatch(new SelectDataReportByName(action.payload));
            }
        }
    }

    @Action(SelectFeatureReportByName)
    selectFeatureReportByName(
        { getState, setState, dispatch }: StateContext<ReportStateModel>,
        action: SelectFeatureReportByName
    ) {
        const state: ReportStateModel = DashGisUtil.clone(getState());
        const report = this.findReport(action.payload.reportName, state);
        if (report) {
            state.selectedFeatureReports[report.definition.layer] = report;
            state.activeFeatureReports = {};
            state.activeFeatureReports[report.definition.layer] = [report];
            if (action.payload.keys) {
                const keysPayload: SetFeatureReportKeysPayload = {
                    keys: action.payload.keys,
                    layer: report.definition.layer,
                };
                dispatch(new SetFeatureReportKeys(keysPayload));
            }
        }
        setState(state);
    }

    @Action(SelectDataReportByName)
    selectDataReportByName({ getState, setState }: StateContext<ReportStateModel>, action: SelectDataReportByName) {
        const state: ReportStateModel = DashGisUtil.clone(getState());
        const report = this.findReport(action.payload.reportName, state);
        if (report) {
            const reportOptions: DataReportLaunchOptions = report;
            if (action.payload.filters) {
                reportOptions.filters = action.payload.filters;
            }
            state.selectedDataReport = reportOptions;
            state.activeDataReports = [reportOptions];
        }
        setState(state);
    }

    @Action(SetPagingOptions)
    setPagingOptions({ getState, setState }: StateContext<ReportStateModel>, action: SetPagingOptions) {
        const pageSizes = this.store.selectSnapshot<TableLimits>(AppConfigState.pageSizeOptions);
        const defaultPaging: PagingOptions = {
            page: 0,
            limit: pageSizes.values[0],
        };
        setState(
            patch<ReportStateModel>({
                options: iif<ReportDisplayOptionsDictionary>(
                    (d) => d.hasOwnProperty(action.payload.id),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: iif<ReportOptionsDictionary>(
                            (d) => d.hasOwnProperty(action.payload.reportName),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: patch<ReportOptions>({
                                    paging: { ...defaultPaging, ...action.payload.pagingOptions },
                                }),
                            }),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: { paging: { ...defaultPaging } },
                            })
                        ),
                    }),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: {
                            [action.payload.reportName]: { paging: { ...defaultPaging } },
                        },
                    })
                ),
            })
        );
    }

    @Action(SetSortOptions)
    setSortOptions({ setState }: StateContext<ReportStateModel>, action: SetSortOptions) {
        const defaultSort: SortOptions = {
            column: null,
            direction: 'asc',
        };
        setState(
            patch<ReportStateModel>({
                options: iif<ReportDisplayOptionsDictionary>(
                    (d) => d.hasOwnProperty(action.payload.id),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: iif<ReportOptionsDictionary>(
                            (d) => d.hasOwnProperty(action.payload.reportName),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: patch<ReportOptions>({
                                    sort: action.payload.sortOptions,
                                }),
                            }),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: { sort: { ...defaultSort } },
                            })
                        ),
                    }),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: {
                            [action.payload.reportName]: { sort: { ...defaultSort } },
                        },
                    })
                ),
            })
        );
    }

    @Action(ResetReportPagingAndSort)
    resetPagingAndSortOptions({ getState, setState }: StateContext<ReportStateModel>, action: ResetReportPagingAndSort) {
        const pageSizes = this.store.selectSnapshot<TableLimits>(AppConfigState.pageSizeOptions);
        const defaultPaging: PagingOptions = {
            page: 0,
            limit: pageSizes.values[0],
        };
        const defaultSort: SortOptions = {
            column: null,
            direction: null,
        };
        const state: ReportStateModel = DashGisUtil.clone(getState());
        Object.keys(state.options)
            .filter((k) => k === action.payload.id)
            .forEach((k) => {
                const currentOptions = state.options[k] || {};
                const currentPaging = currentOptions.paging;
                const currentSort = currentOptions.sort;
                currentOptions.paging = { ...defaultPaging, ...currentPaging, ...{ page: 0 } };
                currentOptions.sort = { ...defaultSort, ...currentSort, ...{ column: null, direction: null } };
                state.options[k] = currentOptions;
            });
        setState(state);
    }

    @Action(SetFilterOptions)
    setFilterOptions({ setState, dispatch }: StateContext<ReportStateModel>, action: SetFilterOptions) {
        setState(
            patch<ReportStateModel>({
                options: iif<ReportDisplayOptionsDictionary>(
                    (d) => d.hasOwnProperty(action.payload.id),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: iif<ReportOptionsDictionary>(
                            (d) => d.hasOwnProperty(action.payload.reportName),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: patch<ReportOptions>({
                                    filters: action.payload.filterOptions,
                                }),
                            }),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: { filters: action.payload.filterOptions },
                            })
                        ),
                    }),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: {
                            [action.payload.reportName]: { filters: action.payload.filterOptions },
                        },
                    })
                ),
            })
        );
        // changing the filter resets page to 0
        return dispatch(
            new SetPagingOptions({
                id: action.payload.id,
                reportName: action.payload.reportName,
                pagingOptions: {},
            })
        );
    }

    @Action(ClearReportFilters)
    clearReportFilters({setState, getState}: StateContext<ReportStateModel>, action: ClearReportFilters) {
        const newState = DashGisUtil.clone(getState());
        const options = newState.options[action.payload.id];
        if (!options) {
            return;
        }
        const report = options[action.payload.reportName];
        if (!report) {
            return;
        }
        report.filters = report.filters.map<FilterOptions>(
            (filter, i, arr) => ({field: filter.field, type: filter.type} as FilterOptions)
        );
        setState(newState);
    }

    @Action(EnableTableEdit)
    enableTableEdit({ setState }: StateContext<ReportStateModel>, action: EnableTableEdit) {
        setState(
            patch<ReportStateModel>({
                options: iif<ReportDisplayOptionsDictionary>(
                    (d) => d.hasOwnProperty(action.payload.id),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: iif<ReportOptionsDictionary>(
                            (d) => d.hasOwnProperty(action.payload.reportName),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: patch<ReportOptions>({
                                    tableEditEnabled: true,
                                }),
                            }),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: { tableEditEnabled: true },
                            })
                        ),
                    }),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: {
                            [action.payload.reportName]: { tableEditEnabled: true },
                        },
                    })
                ),
            })
        );
    }

    @Action(DisableTableEdit)
    disableTableEdit({ setState }: StateContext<ReportStateModel>, action: DisableTableEdit) {
        setState(
            patch<ReportStateModel>({
                options: iif<ReportDisplayOptionsDictionary>(
                    (d) => d.hasOwnProperty(action.payload.id),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: iif<ReportOptionsDictionary>(
                            (d) => d.hasOwnProperty(action.payload.reportName),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: patch<ReportOptions>({
                                    tableEditEnabled: false,
                                }),
                            }),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: { tableEditEnabled: false },
                            })
                        ),
                    }),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: {
                            [action.payload.reportName]: { tableEditEnabled: false },
                        },
                    })
                ),
            })
        );
    }

    @Action(ClearReportOptions)
    clearReportOptions({ getState, setState }: StateContext<ReportStateModel>, action: ClearReportOptions) {
        const state: ReportStateModel = DashGisUtil.clone(getState());
        if (action.payload.reportName) {
            delete state.options?.[action.payload.id]?.[action.payload.reportName];
        } else {
            delete state.options?.[action.payload.id];
        }
        setState(state);
    }

    @Action(SetParcelImageUrl)
    setParcelImageUrl({ getState, setState }: StateContext<ReportStateModel>, action: SetParcelImageUrl) {
        setState(
            patch<ReportStateModel>({
                options: iif<ReportDisplayOptionsDictionary>(
                    (d) => d.hasOwnProperty(action.payload.id),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: iif<ReportOptionsDictionary>(
                            (d) => d.hasOwnProperty(action.payload.reportName),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: patch<ReportOptions>({
                                    parcelDisplay: patch<ParcelDisplayOptions>({
                                        url: action.payload.url,
                                    }),
                                }),
                            }),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: {
                                    parcelDisplay: { url: action.payload.url },
                                },
                            })
                        ),
                    }),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: {
                            [action.payload.reportName]: {
                                parcelDisplay: { url: action.payload.url },
                            },
                        },
                    })
                ),
            })
        );
    }

    @Action(SetParcelPano)
    setParcelPanoId({ setState }: StateContext<ReportStateModel>, action: SetParcelPano) {
        setState(
            patch<ReportStateModel>({
                options: iif<ReportDisplayOptionsDictionary>(
                    (d) => d.hasOwnProperty(action.payload.id),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: iif<ReportOptionsDictionary>(
                            (d) => d.hasOwnProperty(action.payload.reportName),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: patch<ReportOptions>({
                                    parcelDisplay: patch<ParcelDisplayOptions>({
                                        pano: action.payload.pano,
                                        heading: action.payload.heading,
                                    }),
                                }),
                            }),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: {
                                    parcelDisplay: {
                                        pano: action.payload.pano,
                                        heading: action.payload.heading,
                                    },
                                },
                            })
                        ),
                    }),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: {
                            [action.payload.reportName]: {
                                parcelDisplay: {
                                    pano: action.payload.pano,
                                    heading: action.payload.heading,
                                },
                            },
                        },
                    })
                ),
            })
        );
    }

    @Action(SetParcelDisplayAddress)
    setParcelDisplayAddress({ getState, setState }: StateContext<ReportStateModel>, action: SetParcelDisplayAddress) {
        setState(
            patch<ReportStateModel>({
                options: iif<ReportDisplayOptionsDictionary>(
                    (d) => d.hasOwnProperty(action.payload.id),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: iif<ReportOptionsDictionary>(
                            (d) => d.hasOwnProperty(action.payload.reportName),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: patch<ReportOptions>({
                                    parcelDisplay: patch<ParcelDisplayOptions>({
                                        address: action.payload.address,
                                    }),
                                }),
                            }),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: {
                                    parcelDisplay: {
                                        address: action.payload.address,
                                    },
                                },
                            })
                        ),
                    }),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: {
                            [action.payload.reportName]: {
                                parcelDisplay: {
                                    address: action.payload.address,
                                },
                            },
                        },
                    })
                ),
            })
        );
    }

    @Action(SetParcelImageSize)
    setParcelImageSize({ getState, setState }: StateContext<ReportStateModel>, action: SetParcelImageSize) {
        setState(
            patch<ReportStateModel>({
                options: iif<ReportDisplayOptionsDictionary>(
                    d => d.hasOwnProperty(action.payload.id),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: iif<ReportOptionsDictionary>(
                            d => d.hasOwnProperty(action.payload.reportName),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: patch<ReportOptions>({
                                    parcelDisplay: patch<ParcelDisplayOptions>({
                                        imageSize: {
                                            width: action.payload.width,
                                            height: action.payload.height,
                                        }
                                    }),
                                })
                            }),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: {
                                    parcelDisplay: {
                                        imageSize: {
                                            width: action.payload.width,
                                            height: action.payload.height,
                                        }
                                    }
                                },
                            })
                        )
                    }),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: {
                            [action.payload.reportName]: {
                                parcelDisplay: {
                                    imageSize: {
                                        width: action.payload.width,
                                        height: action.payload.height,
                                    }
                                }
                            },
                        }
                    })
                ),
            })
        );
    }

    @Action(ClearParcelDisplayOptions)
    clearParcelDisplayOptions({ setState }: StateContext<ReportStateModel>, action: ClearParcelDisplayOptions) {
        setState(
            patch<ReportStateModel>({
                options: iif<ReportDisplayOptionsDictionary>(
                    d => d.hasOwnProperty(action.payload.id),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: iif<ReportOptionsDictionary>(
                            d => d.hasOwnProperty(action.payload.reportName),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: patch<ReportOptions>({
                                    parcelDisplay: {},
                                })
                            }),
                            patch<ReportOptionsDictionary>({
                                [action.payload.reportName]: {
                                    parcelDisplay: {}
                                },
                            })
                        )
                    }),
                    patch<ReportDisplayOptionsDictionary>({
                        [action.payload.id]: {
                            [action.payload.reportName]: {
                                parcelDisplay: {}
                            },
                        }
                    })
                ),
            })
        );
    }

    @Action(RequestVideoUrl)
    requestVideoUrl({ dispatch }: StateContext<ReportStateModel>, action: RequestVideoUrl) {
        return this.rest.apiGet<DirectUrlResponse>('video', { key: action.payload.videoSource }).pipe(
            map(
                (response): VideoUrl => ({
                    url: response.url,
                    headers: response.headers,
                })
            ),
            concatMap((url) => {
                if (url) {
                    return dispatch(new LaunchVideo({ url }));
                }
            }),
            catchError((err: HttpErrorResponse, caught) => {
                const content = err?.error?.content;
                if (err.status >= 500 || (content?.fatal && content.fatal === true)) {
                    return dispatch(new ShowError(
                        content?.message ?? "There was an error requesting the video. Please contact support."
                    ));
                }
                return of(false);
            })
        );
    }

    @Action(LaunchVideo)
    launchVideo(ctx: StateContext<ReportStateModel>, action: LaunchVideo) {

    }

    @Action(RefreshFilterFields)
    refreshFilterFields() {
        // no action
    }

    private findReport(name: string, state: ReportStateModel): ReportLaunchOptions {
        const featureReports = state.definitions.feature;
        const layers = Object.getOwnPropertyNames(featureReports);
        for (const layer of layers) {
            const featureReport = featureReports[layer].find((r) => r.name === name);
            if (featureReport) {
                return { definition: featureReport };
            }
        }

        const dataReports = state.definitions.data;
        const dataReport = dataReports.find((d) => d.name === name);
        if (dataReport) {
            return { definition: dataReport };
        }
    }

    @Selector()
    static activeFeatureReports(state: ReportStateModel) {
        return state.activeFeatureReports;
    }

    @Selector()
    static activeDataReports(state: ReportStateModel) {
        return state.activeDataReports;
    }

    @Selector()
    static selectedDataReport(state: ReportStateModel) {
        return state.selectedDataReport;
    }

    @Selector()
    static featureReports(state: ReportStateModel) {
        return state.definitions.feature;
    }

    @Selector()
    static featureReportsLoaded(state: ReportStateModel) {
        return state.definitions.featureReportsLoaded;
    }

    @Selector()
    static selectedFeatureReport(state: ReportStateModel) {
        return (layer) => state.selectedFeatureReports[layer];
    }

    @Selector()
    static selectedLayerReports(state: ReportStateModel) {
        return (layer: string) => state.selectedFeatureReports[layer] || [];
    }

    @Selector()
    static featureReportKeys(state: ReportStateModel) {
        return (layer: string) => state.featureReportKeys[layer] || [];
    }

    @Selector()
    static loadingFeatureReports(state: ReportStateModel) {
        return state.loadingFeatureReports;
    }

    @Selector()
    static dataReports(state: ReportStateModel): DataReportDefinitionCollection {
        return state.definitions.data;
    }

    @Selector()
    static dataReportsLoaded(state: ReportStateModel) {
        return state.definitions.dataReportsLoaded;
    }

    @Selector()
    static reportByName(state: ReportStateModel) {
        return (reportName: string) => {
            // search data reports
            for (const def of state.definitions.data) {
                if (def.name === reportName) {
                    return def;
                }
            }
            // search feature reports
            for (const layerName in state.definitions.feature) {
                if (state.definitions.feature.hasOwnProperty(layerName)) {
                    const definitions = state.definitions.feature[layerName];
                    for (const def of definitions) {
                        if (def.name === reportName) {
                            return def;
                        }
                    }
                }
            }
            return null;
        };
    }

    @Selector()
    static paging(state: ReportStateModel) {
        return (id: string, reportName: string) => {
            if (state.options[id]?.[reportName]?.paging) {
                return state.options[id][reportName].paging;
            }
        };
    }

    @Selector()
    static sorting(state: ReportStateModel) {
        return (id: string, reportName: string) => {
            if (state.options[id]?.[reportName]?.sort) {
                return state.options[id][reportName].sort;
            }
        };
    }
    @Selector()
    static filters(state: ReportStateModel) {
        return (id: string, reportName: string) => {
            if (state.options[id]?.[reportName]?.filters) {
                return state.options[id][reportName].filters.map((filter) => {
                    if (filter.type === 'date') {
                        const newFilter = { ...filter };
                        if (filter.min) {
                            newFilter.min = DashGisUtil.reconstructDateTime(filter.min);
                        }
                        if (filter.max) {
                            newFilter.max = DashGisUtil.reconstructDateTime(filter.max);
                        }
                        return newFilter;
                    }
                    return filter;
                });
            }
            return [];
        };
    }

    @Selector()
    static tableEditEnabled(state: ReportStateModel) {
        return (id: string, reportName: string) => {
            if (state.options[id]?.[reportName]) {
                return state.options[id][reportName]?.tableEditEnabled || false;
            }
            return false;
        };
    }

    @Selector()
    static parcelDisplayOptions(state: ReportStateModel) {
        return (id: string, reportName: string) => {
            if (state.options[id]?.[reportName]?.parcelDisplay) {
                return state.options[id][reportName].parcelDisplay;
            }
            return null;
        };
    }

    @Selector()
    static parcelImageUrl(state: ReportStateModel) {
        return (id: string, reportName: string) => {
            if (state.options[id]?.[reportName]?.parcelDisplay) {
                return state.options[id][reportName].parcelDisplay.url;
            }
        };
    }

    @Selector()
    static parcelDisplayAddress(state: ReportStateModel) {
        return (id: string, reportName: string) => {
            if (state.options[id]?.[reportName]?.parcelDisplay) {
                return state.options[id][reportName].parcelDisplay.address;
            }
        };
    }

    @Selector()
    static parcelImageSize(state: ReportStateModel): (id: string, reportName: string) => ImageSize {
        return (id: string, reportName: string) => {
            if (state.options[id]?.[reportName]?.parcelDisplay) {
                return state.options[id][reportName].parcelDisplay.imageSize;
            }
        };
    }

    private static updateSubState(state: any, subStateKey: string, id: string, optionKey: string, value: any) {
        // state is immutable, so clone it first
        const newState = DashGisUtil.clone(state);
        const currentSubState = newState[subStateKey][id] || {};
        const currentOption = currentSubState[optionKey];

        // do not attempt to merge primitive values
        if (typeof value !== 'object' && typeof value !== 'function') {
            currentSubState[optionKey] = value;
        } else {
            currentSubState[optionKey] = merge(currentOption, value);
        }
        return newState;
    }
}
